import * as React from "react";
import { Controller, type Control } from "react-hook-form";
import { Eye, EyeOff, User, KeyRound, InfoIcon } from "lucide-react";
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
  InputGroupButton,
} from "@/taskpane/components/ui/input-group";
import { Field, FieldContent, FieldError } from "@/taskpane/components/ui/field";

export interface LoginFieldValues {
  email: string;
  password: string;
}

export const EmailField: React.FC<{
  control: Control<LoginFieldValues>;
  isPending: boolean;
}> = ({ control, isPending }) => (
  <Controller
    name="email"
    control={control}
    render={({ field, fieldState }) => (
      <Field data-invalid={fieldState.invalid} className="gap-1">
        <FieldContent>
          <InputGroup aria-invalid={fieldState.invalid}>
            <InputGroupAddon>
              <User className="text-neutral-200" />
            </InputGroupAddon>
            <InputGroupInput
              type="email"
              id="email"
              placeholder="Enter your email"
              {...field}
              disabled={isPending}
              aria-describedby={fieldState.invalid ? "email-error" : undefined}
              aria-invalid={fieldState.invalid}
              autoComplete="email"
              className="placeholder:text-neutral-500 text-sm py-3 px-4"
            />
          </InputGroup>
          {fieldState.invalid && (
            <div className="flex items-center gap-1 mt-1" id="email-error">
              <InfoIcon className="inline size-3 text-danger-200" />
              <FieldError className="leading-4" errors={[fieldState.error]} />
            </div>
          )}
        </FieldContent>
      </Field>
    )}
  />
);

export const PasswordField: React.FC<{
  control: Control<LoginFieldValues>;
  isPending: boolean;
  showPassword: boolean;
  onToggleVisibility: () => void;
}> = ({ control, isPending, showPassword, onToggleVisibility }) => (
  <Controller
    name="password"
    control={control}
    render={({ field, fieldState }) => (
      <Field data-invalid={fieldState.invalid} className="gap-1">
        <FieldContent>
          <InputGroup aria-invalid={fieldState.invalid}>
            <InputGroupAddon>
              <KeyRound className="text-neutral-200" />
            </InputGroupAddon>
            <InputGroupInput
              type={showPassword ? "text" : "password"}
              id="password"
              placeholder="Enter your password"
              {...field}
              disabled={isPending}
              aria-describedby={fieldState.invalid ? "password-error" : undefined}
              aria-invalid={fieldState.invalid}
              autoComplete="current-password"
              className="placeholder:text-neutral-500 text-sm py-3 px-4"
            />
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                onClick={onToggleVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                disabled={isPending}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          {fieldState.invalid && (
            <div className="flex items-center gap-1 mt-1" id="password-error">
              <InfoIcon className="inline size-3 text-danger-200" />
              <FieldError className="leading-4" errors={[fieldState.error]} />
            </div>
          )}
        </FieldContent>
      </Field>
    )}
  />
);
